import { Breadcrumb } from '@/components/Breadcrumb';
import { CardRichTextRenderer } from '@/components/Cms/Card/components/BaseCardType/RichTextEditor/CardRichTextRenderer';
import { Cta } from '@/components/Cms/Card/components/CTA';
import { Heading } from '@/components/Cms/Heading';
import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import { ListingFilters } from '@/components/ListingFilters';
import type {
  ContentfulCardFragment,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useHasActiveFilters } from '@/integrations/algolia/helpers';
import { getSubheadingType } from '@/lib/title';
import { theme } from '@/theme';

import type { HeroProductListingProps } from '../../types';
import { useGenerateBreadCrumbItems } from '../ListingType.tsx/helpers';

import * as S from './EditorialType.styles';

export const EditorialType = ({
  backgroundColor,
  backgroundColorOpacity,
  contentAlignment,
  heading,
  heroProductListingText: text,
  heroProductListingTitle: title,
  media,
  openUrl2InNewTab,
  openUrlInNewTab,
  pageName,
  showBreadcrumbs,
  showFilters,
  subheader,
  subheaderIsCapitalized,
  textAlignment,
  textColor,
  type,
  url,
  url1EventType,
  url2,
  url2EventType,
  url2Text,
  url2Type,
  urlDirection,
  urlPosition,
  urlText,
  urlType,
  visibleInDesktop = true,
  visibleInMobile = true,
}: HeroProductListingProps) => {
  const { deviceType } = useDeviceDetect();

  const breadCrumbItems = useGenerateBreadCrumbItems({
    heading,
    type,
    pageName,
  });
  const urlIsButton = !!urlText && !urlType?.includes('link');
  const url2IsButton = !!url2Text && !url2Type?.includes('link');

  const hasButton = urlIsButton || url2IsButton;
  const hasActiveFilters = useHasActiveFilters();
  return (
    <>
      <S.Wrapper
        $visibleInDesktop={visibleInDesktop}
        $visibleInMobile={visibleInMobile}
        data-test-id='EditorialTypeWrapper'
        disableGutters
        maxWidth='xl'
      >
        <Breadcrumb.Wrapper>
          {showBreadcrumbs && (
            <Breadcrumb
              data-test-id='EditorialTypeBreadcrumb'
              items={breadCrumbItems}
            />
          )}
        </Breadcrumb.Wrapper>

        <S.Header
          $backgroundColor={backgroundColor?.value}
          $backgroundColorOpacity={backgroundColorOpacity}
          $contentAlignment={contentAlignment}
        >
          {media && (
            <S.MediaWrapper>
              <MediaWrapper
                {...(media as ContentfulImageWithFocalPointFragment)}
                mediaWrapperType='Auto'
                preload={true}
                sizes='100vw'
                useVideo={false}
              />
            </S.MediaWrapper>
          )}
          <S.Heading $textAlignment={textAlignment}>
            <Heading
              Heading={
                (title as ContentfulCardFragment['placeholderHeading']) ??
                undefined
              }
              color={title?.mainColor ?? theme.colors.default}
              data-test-id='EditorialTypeHeading'
              mobileTextAlign='center'
              shouldApplyBottomPadding={false}
            />
            {subheader && (
              <S.Subheading
                $isCapitalized={subheaderIsCapitalized}
                $textColor={textColor}
                as={getSubheadingType(
                  (title?.headingSeoTag ?? 'h3') as keyof HTMLElementTagNameMap,
                )}
                data-test-id='EditorialTypeSubheading'
              >
                {subheader}
              </S.Subheading>
            )}

            {text && (
              <CardRichTextRenderer
                backgroundColor={backgroundColor?.value}
                characterLimit={deviceType === 'mobile' ? 150 : 250}
                enableShowMore={true}
                showMoreType='icon'
                text={text}
                textColor={textColor}
                type={type}
              />
            )}

            <S.CTAWrappersContainer
              $hasButton={hasButton}
              $textAlignment={textAlignment ?? 'center'}
              $urlDirection={urlDirection}
              $urlPosition={urlPosition}
            >
              {urlText && (
                <S.CTAWrapper $isButton={urlIsButton}>
                  <Cta
                    fluid
                    openUrlInNewTab={openUrlInNewTab}
                    url={url}
                    urlEventType={url1EventType as string}
                    urlText={urlText}
                    urlType={urlType}
                  />
                </S.CTAWrapper>
              )}
              {url2Text && (
                <S.CTAWrapper $isButton={url2IsButton}>
                  <Cta
                    fluid
                    openUrlInNewTab={openUrl2InNewTab}
                    url={url2}
                    urlEventType={url2EventType as string}
                    urlText={url2Text}
                    urlType={url2Type}
                  />
                </S.CTAWrapper>
              )}
            </S.CTAWrappersContainer>
          </S.Heading>
        </S.Header>
      </S.Wrapper>

      {!!showFilters && (
        <S.FiltersWrapper
          $visibleInDesktop={visibleInDesktop}
          $visibleInMobile={visibleInMobile}
        >
          <ListingFilters
            collectionData={[]}
            data-test-id='EditorialTypeListingFilters'
            forceMount={hasActiveFilters}
            showCollectionFilter={pageName === 'all'}
            showMaterialsFilter={pageName === 'all'}
          />
        </S.FiltersWrapper>
      )}
    </>
  );
};
